import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Keyboard from "react-simple-keyboard";
import "react-simple-keyboard/build/css/index.css";
import "../../style.css";
import axios from "axios";
import debounce from "lodash/debounce";
import { CircularProgress } from "@mui/material";

const BADGE_URL = `${process.env.REACT_APP_BACKEND_URL}/assets/Symposium_Badge_2026.png`;

const SearchPrint = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [shift, setShift] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const rowsPerPage = 15;

  const keyboardRef = useRef(null);

  const fetchData = async (query = "", page = 0) => {
    try {
      setLoading(true);
      const endpoint = query
        ? `/api/form/search?query=${query}&page=${page + 1}&limit=${rowsPerPage}`
        : `/api/form/?page=${page + 1}&limit=${rowsPerPage}`;

      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`);
      setData(response.data.data || []);
      setTotalCount(response.data.total || 0);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch initial data on load
  useEffect(() => {
    fetchData("", 0);
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((query) => {
      fetchData(query, 0);
    }, 500),
    []
  );

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(0);
    debouncedSearch(value);
  };

  const handlePageChange = (_, newPage) => {
    setCurrentPage(newPage);
    fetchData(searchTerm, newPage);
  };

  const handlePrint = (user) => {
    const printWindow = window.open("", "_blank");
    const printContent = `
<!DOCTYPE html>
<html>
  <head>
    <title>Visitor Badge</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body {
        width: 100%;
        height: 100%;
        font-family: Arial, sans-serif;
      }
      body {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 100vh;
        text-align: center;
      }
      .print-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }
      .visitor-name {
        font-size: 22px;
        font-weight: bold;
        color: #000;
        letter-spacing: 1px;
        word-break: break-word;
      }
      .visitor-company {
        font-size: 16px;
        color: #333;
        word-break: break-word;
      }
      .visitor-qr {
        width: 150px;
        height: 150px;
        border: 1px solid #ccc;
        margin-top: 5px;
      }
      @media print {
        @page { margin: 0; size: auto; }
        html, body {
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    </style>
  </head>
  <body>
    <div class="print-content">
      <p class="visitor-name">${`${user.firstName} ${user.lastName}`.trim().toUpperCase()}</p>
      <p class="visitor-company">${user.companyName || ""}</p>
      <img class="visitor-qr" src="${user.qrCode}" alt="QR Code" id="qr-img" />
    </div>
    <script>
      const img = document.getElementById("qr-img");
      if (img.complete) {
        window.print();
        window.close();
      } else {
        img.onload = () => { window.print(); window.close(); };
      }
    </script>
  </body>
</html>
    `;
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };


  // Keyboard handlers
  const onKeyboardChange = (input) => {
    handleSearch(input);
  };

  const toggleShift = () => setShift((prev) => !prev);

  const closeKeyboard = () => setKeyboardVisible(false);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );

  return (
    <div className="search-and-print-layer">
      <h2>Search and Print</h2>

      <TextField
        variant="outlined"
        label="Search by Name / Company Name / Phone"
        value={searchTerm}
        onFocus={() => setKeyboardVisible(true)}
        fullWidth
        style={{ marginBottom: "12px" }}
        onChange={(e) => handleSearch(e.target.value)}
      />

      {keyboardVisible && (
        <Box sx={{ position: "relative", mb: 2 }}>
          {/* Close keyboard button */}
          <Tooltip title="Close keyboard">
            <IconButton
              size="small"
              onClick={closeKeyboard}
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 10,
                bgcolor: "#fff",
                border: "1px solid #ccc",
                "&:hover": { bgcolor: "#f5f5f5" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Keyboard
            ref={keyboardRef}
            onChange={onKeyboardChange}
            inputName="search"
            input={{ search: searchTerm }}
            layout={{
              default: shift
                ? [
                  "! @ # $ % ^ & * ( ) _",
                  "Q W E R T Y U I O P",
                  "{shift} A S D F G H J K L",
                  "Z X C V B N M {bksp}",
                  "{space}",
                ]
                : [
                  "1 2 3 4 5 6 7 8 9 0",
                  "q w e r t y u i o p",
                  "{shift} a s d f g h j k l",
                  "z x c v b n m {bksp}",
                  "{space}",
                ],
            }}
            display={{
              "{bksp}": "⌫",
              "{space}": "Space",
              "{shift}": "⇧",
            }}
            onKeyPress={(button) => {
              if (button === "{shift}") toggleShift();
            }}
          />
        </Box>
      )}

      <TableContainer component={Paper} style={{ marginBottom: "20px" }}>
        <Table>
          <TableHead>
            <TableRow>
              {[
                "Visitor ID",
                "Name",
                "Email",
                "Company Name",
                "Phone",
                "Designation",
                "Source",
                "Created At",
                "Action",
              ].map((heading) => (
                <TableCell
                  key={heading}
                  align="center"
                  sx={{ fontWeight: "bold" }}
                >
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {searchTerm ? "No results found for your search." : "No visitors registered yet."}
                </TableCell>
              </TableRow>
            ) : (
              data.map((user) => (
                <TableRow key={user._id}>
                  <TableCell align="center">{user.visitorId}</TableCell>
                  <TableCell align="center">
                    {`${user.firstName} ${user.lastName}`.trim()}
                  </TableCell>
                  <TableCell align="center">{user.email.toLowerCase()}</TableCell>
                  <TableCell align="center">{user.companyName}</TableCell>
                  <TableCell align="center">{user.mobile}</TableCell>
                  <TableCell align="center">
                    {user.designation?.toUpperCase() || "—"}
                  </TableCell>
                  <TableCell align="center">{user.source || "—"}</TableCell>
                  <TableCell align="center">{user.createdAt ? new Date(user.createdAt).toLocaleString() : "—"}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handlePrint(user)}
                    >
                      Print
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={currentPage}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </div>
  );
};

export default SearchPrint;
